/**
 * @fileoverview Edit Modal component for managing lead/client information.
 * Provides functionality for editing client details, managing employee assignments,
 * handling payouts, and syncing analytics.
 */

/**
 * @typedef {Object} EditModalProps
 * @property {boolean} isOpen - Whether the modal is open
 * @property {Function} onClose - Callback function to close the modal
 * @property {Object} data - Client data to edit
 * @property {Function} onSave - Callback function called after successful save
 * @property {Array<Object>} partners - List of available partners
 */

/**
 * @typedef {Object} Employee
 * @property {string} _id - Employee ID
 * @property {string} name - Employee name
 * @property {string} email - Employee email
 * @property {string} role - Employee role
 */

/**
 * @typedef {Object} PayoutInfo
 * @property {string} payee_id - Payee ID
 * @property {number} percentage - Payout percentage
 * @property {string} name - Payee name
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faProjectDiagram,
  faShareNodes,
  faFileContract,
  faMoneyBill,
  faChartLine,
  faCheckCircle,
  faCamera,
  faVideo,
  faPlay,
  faGhost,
  faArrowRight,
  faSpinner,
  faHandshake,
  faSync,
  faComments
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import './styles/EditLeadModal.css';
import { useAuth } from '../../contexts/AuthContext';
import { fetchAuthSession } from 'aws-amplify/auth';
import { canViewFinancials } from '../../utils/permissions';
import OnboardingProgressBar from './OnboardingProgressBar';
import OnboardingModal from './OnboardingModal';

/**
 * Edit Modal component for managing client information and settings.
 * Features include:
 * - Editing client personal and social media information
 * - Managing employee assignments
 * - Handling payout configurations
 * - Syncing analytics and social media stats
 * 
 * @param {EditModalProps} props - Component props
 * @returns {React.ReactElement} The rendered modal
 */
const EditModal = ({ isOpen, onClose, data, onSave, partners }) => {
  const { user } = useAuth();
  
  // Add debugging logs
  console.log('Auth user:', user);
  
  const [isAdmin, setIsAdmin] = useState(false);

  // Add this useEffect to check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const { tokens } = await fetchAuthSession();
        const groups = tokens.accessToken.payload['cognito:groups'] || [];
        console.log('Token groups:', groups);
        setIsAdmin(groups.includes('ADMIN'));
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      }
    };
    
    if (isOpen) {
      checkAdminStatus();
    }
  }, [isOpen]);

  const [formData, setFormData] = useState({
    First_Legal_Name: '',
    Last_Legal_Name: '',
    Email_Address: '',
    Phone_Number: '',
    email_alias: '',
    aws_email: '',
    aws_password: '',
    Stage_Name: '',
    DOB: '',
    IG_Username: '',
    IG_Followers: 0,
    IG_Verified: false,
    IG_Engagement: 0,
    IG_Rank: 0,
    IG_Views_Rank: 0,
    TT_Username: '',
    TT_Followers: 0,
    TT_Verified: false,
    TT_Rank: 0,
    TT_Views_Rank: 0,
    YT_Username: '',
    YT_Followers: 0,
    YT_Verified: false,
    YT_Rank: 0,
    YT_Views_Rank: 0,
    Snap_Username: '',
    Snap_Followers: 0,
    Snap_Star: false,
    Snap_Monetized: false,
    is_contractout: false,
    is_signed: false,
    is_groupchat: false,
    is_dead: false,
    client_id: '',
    snap_id: '',
    assigned_employees: [],
    // New bio fields
    client_bio: '',
    content_type: '',
    age: '',
    gender: '',
    location: '',
    interests: [],
    brand_categories: [],
    audience_demographics: {},
    collaboration_history: [],
    rate_card: {},
    bio_generated_at: null,
    bio_generated_by: '',
    client_payout_percent: 0,
    company_net_percent: 100,
    spotlight_frequency: 2
  });
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [matchingEmployees, setMatchingEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assignedEmployees, setAssignedEmployees] = useState([]);
  const [profileName, setProfileName] = useState('');
  const [syncStatus, setSyncStatus] = useState('');
  const [payoutInfo, setPayoutInfo] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [activeSection, setActiveSection] = useState('personal');
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' });
  const [hasChanges, setHasChanges] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [selectedPayee, setSelectedPayee] = useState(null);
  const [splitPercentage, setSplitPercentage] = useState('');
  const [splits, setSplits] = useState([]);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [onboardingModalOpen, setOnboardingModalOpen] = useState(false);
  const [payees, setPayees] = useState([]);
  const [isGeneratingBio, setIsGeneratingBio] = useState(false);
  const [snapMonetizationData, setSnapMonetizationData] = useState(null);
  const [loadingSnapData, setLoadingSnapData] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [messagingInfo, setMessagingInfo] = useState(null);
  const [syncingMessaging, setSyncingMessaging] = useState(false);

  // Get all sections
  const allSections = [
    { id: 'personal', label: 'Personal Information', icon: faUser },
    { id: 'bio', label: 'Brand Deals', icon: faHandshake },
    { id: 'socials', label: 'Social Media', icon: faShareNodes },
    { id: 'snap-monetization', label: 'Snap Monetization', icon: faGhost },
    { id: 'messaging', label: 'Messaging', icon: faComments },
    { id: 'contract', label: 'Contract Status', icon: faFileContract },
    { id: 'payout', label: 'Payout Information', icon: faMoneyBill },
    { id: 'backend', label: 'Backend', icon: faProjectDiagram },
    { id: 'onboarding', label: 'Onboarding Progress', icon: faArrowRight }
  ];

  // Filter sections based on role
  const sections = allSections.filter(section => {
    // Sales users cannot see payout, backend, or snap-monetization
    if (user?.role === 'sales') {
      return !['payout', 'backend', 'snap-monetization'].includes(section.id);
    }
    
    // CSM users cannot see payout or backend (no financial/monetization info)
    if (user?.role === 'csm') {
      return !['payout', 'backend', 'snap-monetization'].includes(section.id);
    }
    
    // Editor users can only see basic info
    if (user?.role === 'editor') {
      return false; // Editors shouldn't be in EditModal anyway
    }
    
    // Manager users cannot see payout (no financial info)
    if (user?.role === 'manager') {
      return section.id !== 'payout';
    }
    
    // Admin can see everything
    return true;
  });

  const messagingPlatforms = [
    { value: 'iMessage', label: 'iMessage' },
    { value: 'WhatsApp', label: 'WhatsApp' },
    { value: 'Telegram', label: 'Telegram' },
    { value: 'SMS', label: 'SMS' }
  ];

  const socialPlatforms = [
    {
      id: 'instagram',
      name: 'Instagram',
      fields: [
        { name: 'IG_Username', label: 'Username', type: 'text' },
        { name: 'IG_Followers', label: 'Followers', type: 'number' },
        { name: 'IG_Engagement', label: 'Engagement Rate', type: 'number', step: '0.01' },
        { name: 'IG_Rank', label: 'Rank', type: 'number' },
        { name: 'IG_Views_Rank', label: 'Views Rank', type: 'number' },
        { name: 'IG_Verified', label: 'Verified', type: 'checkbox' }
      ],
      fetchStats: (username) => fetchSocialStats('instagram', username)
    },
    {
      id: 'tiktok',
      name: 'TikTok',
      fields: [
        { name: 'TT_Username', label: 'Username', type: 'text' },
        { name: 'TT_Followers', label: 'Followers', type: 'number' },
        { name: 'TT_Rank', label: 'Rank', type: 'number' },
        { name: 'TT_Views_Rank', label: 'Views Rank', type: 'number' },
        { name: 'TT_Verified', label: 'Verified', type: 'checkbox' }
      ],
      fetchStats: (username) => fetchSocialStats('tiktok', username)
    },
    {
      id: 'youtube',
      name: 'YouTube',
      fields: [
        { name: 'YT_Username', label: 'Username', type: 'text' },
        { name: 'YT_Followers', label: 'Followers', type: 'number' },
        { name: 'YT_Rank', label: 'Rank', type: 'number' },
        { name: 'YT_Views_Rank', label: 'Views Rank', type: 'number' },
        { name: 'YT_Verified', label: 'Verified', type: 'checkbox' }
      ],
      fetchStats: (username) => fetchSocialStats('youtube', username)
    },
    {
      id: 'snapchat',
      name: 'Snapchat',
      fields: [
        { name: 'Snap_Username', label: 'Username', type: 'text' },
        { name: 'Snap_Followers', label: 'Followers', type: 'number' },
        { name: 'Snap_Star', label: 'Verified', type: 'checkbox' }
      ]
    }
  ];

  /**
   * Loads the list of employees assigned to the client
   * @async
   * @returns {Promise<void>}
   */
  const loadAssignedEmployees = async () => {
    try {
      const { tokens } = await fetchAuthSession();
      
      // Validate client_id exists before making API call
      if (!formData.client_id) {
        console.warn('loadAssignedEmployees called without client_id');
        return;
      }
      
      // Get the client data to get the assigned_employees array
      const response = await axios.get(API_ENDPOINTS.LEADS.GRID, {
        headers: {
          'Authorization': `Bearer ${tokens.accessToken.toString()}`
        }
      });
      
      // Find this specific client
      const clientData = response.data.find(client => client.client_id === formData.client_id);
      if (!clientData) {
        console.error(`Could not find client with ID: "${formData.client_id}" in ${response.data.length} clients`);
        throw new Error(`Could not find client with ID: ${formData.client_id}`);
      }
      
      // Get the full employee details for each assigned employee
      const employeeResponse = await axios.get(API_ENDPOINTS.EMPLOYEES.GET_ASSIGNED, {
        params: {
          user_ids: clientData.assigned_employees?.join(',')
        },
        headers: {
          'Authorization': `Bearer ${tokens.accessToken.toString()}`
        }
      });
      
      console.log('Currently assigned employees:', employeeResponse.data);
      
      // Update the UI with the current assignments
      setAssignedEmployees(employeeResponse.data);
      setFormData(prev => ({
        ...prev,
        assigned_employees: clientData.assigned_employees || []
      }));
      
    } catch (error) {
      console.error('Error loading assigned employees:', error);
    }
  };

  // Load existing assigned employees on modal open
  useEffect(() => {
    // Only load if we have a valid client_id and the modal is open
    if (isOpen && formData.client_id && (formData.monetized_by || formData.referred_by)) {
      loadAssignedEmployees();
    }
  }, [isOpen, formData.client_id, formData.monetized_by, formData.referred_by]);

  /**
   * Assigns an employee to the client
   * @param {Employee} employee - Employee to assign
   * @returns {void}
   */
  const handleAssignEmployee = async (employee) => {
    try {
      const { tokens } = await fetchAuthSession();
      
      // First save the assignment to the server
      const response = await axios.post(
        API_ENDPOINTS.EMPLOYEES.ASSIGN,
        {
          client_id: formData.client_id,
          user_id: employee.user_id
        },
        {
          headers: {
            'Authorization': `Bearer ${tokens.accessToken.toString()}`
          }
        }
      );

      if (response.data.status === 'success') {
        // Only update local state if server save was successful
        setAssignedEmployees(prev => [...prev, employee]);
        setFormData(prev => ({
          ...prev,
          assigned_employees: [...(prev.assigned_employees || []), employee.user_id]
        }));
        setEmployeeSearch('');
        setMatchingEmployees([]);
        toast.success(`Successfully assigned ${employee.first_name} ${employee.last_name}`);
      } else {
        toast.error('Failed to assign employee');
      }
    } catch (error) {
      console.error('Error assigning employee:', error);
      toast.error('Failed to assign employee');
    }
  };

  /**
   * Removes an employee from the client's assignments
   * @param {string} userId - ID of the employee to remove
   * @returns {void}
   */
  const handleRemoveEmployee = async (userId) => {
    try {
      const { tokens } = await fetchAuthSession();
      
      // First remove the assignment on the server
      const response = await axios.delete(
        API_ENDPOINTS.EMPLOYEES.UNASSIGN,
        {
          data: {
            client_id: formData.client_id,
            user_id: userId
          },
          headers: {
            'Authorization': `Bearer ${tokens.accessToken.toString()}`
          }
        }
      );

      if (response.data.status === 'success') {
        // Only update local state if server removal was successful
        setAssignedEmployees(prev => prev.filter(emp => emp.user_id !== userId));
        setFormData(prev => ({
          ...prev,
          assigned_employees: prev.assigned_employees.filter(id => id !== userId)
        }));
        toast.success('Successfully removed employee assignment');
      } else {
        toast.error('Failed to remove employee assignment');
      }
    } catch (error) {
      console.error('Error removing employee assignment:', error);
      toast.error('Failed to remove employee assignment');
    }
  };

  useEffect(() => {
    if (isOpen && data) {
      console.log('Setting form data:', data);
      console.log('Email alias from data:', data.email_alias);
      console.log('Assigned employees from data:', data.assigned_employees);
      
      const initialData = {
        ...data,
        // Make sure we preserve the email_alias from the data
        email_alias: data.email_alias || '',
        Phone_Number: data.Phone_Number || data.Phone || '',
        IG_Followers: data.IG_Followers || 0,
        IG_Verified: Boolean(data.IG_Verified),
        IG_Engagement: data.IG_Engagement || 0,
        TT_Followers: data.TT_Followers || 0,
        TT_Verified: Boolean(data.TT_Verified),
        YT_Followers: data.YT_Followers || 0,
        YT_Verified: Boolean(data.YT_Verified),
        Snap_Followers: data.Snap_Followers || 0,
        Snap_Star: Boolean(data.Snap_Star),
        Snap_Monetized: Boolean(data.Snap_Monetized),
        is_contractout: Boolean(data.is_contractout),
        is_signed: Boolean(data.is_signed),
        is_groupchat: Boolean(data.is_groupchat),
        is_dead: Boolean(data.is_dead),
        assigned_employees: data.assigned_employees || [],
        // Messaging fields - explicitly load from data or reset to defaults
        normalized_phone_number: data.normalized_phone_number || '',
        group_chat_id: data.group_chat_id || '',
        message_location: data.message_location || 'iMessage', // Default to iMessage
        messaging_last_sync: data.messaging_last_sync || null,
        auto_messaging_enabled: data.auto_messaging_enabled !== false, // Default to true
        spotlight_frequency: data.spotlight_frequency ?? 2,
      };
      
      setFormData(initialData);
      setOriginalData(initialData); // Save original data for comparison
      setHasChanges(false); // Reset changes flag
      
      if (data.assigned_employees?.length) {
        loadAssignedEmployees();
      }
      
      if (data.client_id) {
        loadPayoutInfo();
        loadSplitInfo(); // Load split percentages from splits-company collection
        fetchApprovalStatus(data.client_id);
        loadSnapMonetizationData(data.client_id);
      }
    }
  }, [isOpen, data]);

  // Track changes to formData
  useEffect(() => {
    if (originalData && formData) {
      const hasChanged = JSON.stringify(formData) !== JSON.stringify(originalData);
      setHasChanges(hasChanged);
    }
  }, [formData, originalData]);

  /**
   * Loads Snap Monetization form data for the client
   * @async
   * @returns {Promise<void>}
   */
  const loadSnapMonetizationData = async (clientId) => {
    try {
      setLoadingSnapData(true);
      const { tokens } = await fetchAuthSession();
      const response = await axios.get(`${API_ENDPOINTS.SNAP_MONETIZATION.RESPONSES}?client_id=${clientId}`, {
        headers: {
          'Authorization': `Bearer ${tokens.accessToken.toString()}`
        }
      });
      
      // Find the response for this specific client
      const clientData = response.data.find(item => item.client_id === clientId);
      setSnapMonetizationData(clientData || null);
    } catch (error) {
      console.error('Error loading Snap Monetization data:', error);
      setSnapMonetizationData(null);
    } finally {
      setLoadingSnapData(false);
    }
  };

  /**
   * Loads payout information for the client
   * @async
   * @returns {Promise<void>}
   */
  const loadPayoutInfo = async () => {
    try {
      const { tokens } = await fetchAuthSession();
      const response = await axios.get(API_ENDPOINTS.LEADS.PAYOUT_INFO(data.client_id), {
        headers: {
          'Authorization': `Bearer ${tokens.accessToken.toString()}`
        }
      });
      const info = response.data;
      setPayoutInfo(info);
      if (info.payout_email) {
        setFormData(prev => ({ ...prev, payout_email: info.payout_email }));
      }
    } catch (error) {
      console.error('Error loading payout info:', error);
    }
  };

  /**
   * Loads split percentages from splits-company collection
   * @async
   * @returns {Promise<void>}
   */
  const loadSplitInfo = async () => {
    try {
      const { tokens } = await fetchAuthSession();
      const response = await axios.get(API_ENDPOINTS.LEADS.GET_SPLIT_INFO(data.client_id), {
        headers: {
          'Authorization': `Bearer ${tokens.accessToken.toString()}`
        }
      });
      // splits-company returns data directly, not in a splits array
      if (response.data && response.data.client_payout_percent !== undefined && response.data.company_net_percent !== undefined) {
        setFormData(prev => ({
          ...prev,
          client_payout_percent: response.data.client_payout_percent || 0,
          company_net_percent: response.data.company_net_percent || 100
        }));
      }
    } catch (error) {
      console.error('Error loading split info:', error);
      // If no splits found, that's okay - use defaults
    }
  };

  /**
   * Syncs payout information with external systems
   * @async
   * @returns {Promise<void>}
   */
  const handleSyncPayout = async () => {
    try {
      setIsSyncing(true);
      const { tokens } = await fetchAuthSession();
      const response = await axios.post(API_ENDPOINTS.LEADS.SYNC_PAYOUT, {
        client_id: data.client_id,
        payout_email: formData.payout_email
      }, {
        headers: {
          'Authorization': `Bearer ${tokens.accessToken.toString()}`
        }
      });
      
      if (response.data.status === 'success') {
        await loadPayoutInfo();
        toast.success('Payout email synced successfully');
      } else {
        toast.error(response.data.detail || 'Failed to sync payout email');
      }
    } catch (error) {
      console.error('Error syncing payout:', error);
      toast.error('Failed to sync payout email');
    } finally {
      setIsSyncing(false);
    }
  };

  /**
   * Handles changes to form fields
   * @param {React.ChangeEvent<HTMLInputElement>} e - Change event
   * @returns {void}
   */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'spotlight_frequency') {
      const numericValue = Number(value);
      const sanitized = Number.isFinite(numericValue) && numericValue > 0
        ? Math.min(10, Math.max(1, Math.round(numericValue)))
        : 1;
      setFormData(prev => ({
        ...prev,
        spotlight_frequency: sanitized
      }));
      return;
    }

    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'number' ? Number(value) : value
      }));
    }
  };

  /**
   * Handles form submission
   * @async
   * @param {React.FormEvent} e - Form event
   * @returns {Promise<void>}
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const updatedData = { ...formData };
    
    // Backend will handle all spotlight_frequency validation
    
    try {
      setIsSaving(true);
      const { tokens } = await fetchAuthSession();
      
      if (updatedData.client_id) {
        const uploadApprovalResponse = await axios.post(
          API_ENDPOINTS.LEADS.UPDATE_APPROVAL_STATUS,
          { 
            client_id: updatedData.client_id,
            approved: updatedData.approved
          },
          {
            headers: {
              'Authorization': `Bearer ${tokens.accessToken.toString()}`
            }
          }
        );
        console.log('Content approval update response:', uploadApprovalResponse.data);
      }

      // Save split percentages to splits-company collection (same as ContractModal)
      if (updatedData.client_id && 
          (updatedData.client_payout_percent !== undefined || updatedData.company_net_percent !== undefined)) {
        try {
          const splitResponse = await axios.post(
            API_ENDPOINTS.LEADS.SAVE_SPLIT_INFO(),
            {
              clientId: updatedData.client_id,
              client_payout_percent: parseFloat(updatedData.client_payout_percent) || 0,
              company_net_percent: parseFloat(updatedData.company_net_percent) || 100
            },
            {
              headers: {
                'Authorization': `Bearer ${tokens.accessToken.toString()}`
              }
            }
          );
          console.log('Split info saved to splits-company:', splitResponse.data);
        } catch (error) {
          console.error('Error saving split info to splits-company:', error);
          // Don't fail the entire save if split save fails, just log it
        }
      }

      await onSave(updatedData);
      
      // Update original data to match saved data
      setOriginalData(updatedData);
      setHasChanges(false);
      
      // Show success status inside the modal
      setSaveStatus({ type: 'success', message: 'Information saved successfully!' });
      
      // Clear the status after 3 seconds
      setTimeout(() => {
        setSaveStatus({ type: '', message: '' });
      }, 3000);
      
    } catch (error) {
      console.error('Error saving lead:', error);
      setSaveStatus({ type: 'error', message: 'Failed to save changes. Please try again.' });
      
      // Clear error status after 4 seconds
      setTimeout(() => {
        setSaveStatus({ type: '', message: '' });
      }, 4000);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Fetches social media statistics for a platform
   * @async
   * @param {string} platform - Social media platform
   * @param {string} username - Username on the platform
   * @returns {Promise<Object>} Platform statistics
   */
  const fetchSocialStats = async (platform, username) => {
    if (!username || username.length < 3) return;
    
    try {
      const { tokens } = await fetchAuthSession();
      const response = await axios.get(API_ENDPOINTS.SOCIAL_STATS(platform, username), {
        headers: {
          'Authorization': `Bearer ${tokens.accessToken.toString()}`
        }
      });
      console.log(`${platform} response:`, response.data);
      
      if (response.data) {
        console.log(`${platform} statistics:`, response.data.statistics);
        console.log(`${platform} misc:`, response.data.misc);
        console.log(`${platform} ranks:`, response.data.ranks);
        
        switch(platform) {
          case 'instagram':
            const updatedData = {
              IG_Followers: response.data.statistics?.followers || 0,
              IG_Verified: response.data.misc?.sb_verified || false,
              IG_Engagement: response.data.statistics?.engagement_rate || 0,
              IG_Rank: response.data.ranks?.sbrank || 0,
              IG_Views_Rank: response.data.ranks?.following || 0
            };
            console.log('Updating Instagram data:', updatedData);
            setFormData(prev => ({
              ...prev,
              ...updatedData
            }));
            break;
          case 'tiktok':
            setFormData(prev => ({
              ...prev,
              TT_Followers: response.data.statistics?.followers || 0,
              TT_Verified: response.data.misc?.sb_verified || false,
              TT_Rank: response.data.ranks?.sbrank || 0,
              TT_Views_Rank: response.data.ranks?.following || 0
            }));
            break;
          case 'youtube':
            setFormData(prev => ({
              ...prev,
              YT_Followers: response.data.statistics?.subscribers || 0,
              YT_Verified: response.data.misc?.sb_verified || false,
              YT_Rank: response.data.ranks?.sbrank || 0,
              YT_Views_Rank: response.data.ranks?.views || 0
            }));
            break;
        }
      }
    } catch (error) {
      console.error(`Error fetching ${platform} stats:`, error);
      if (error.response?.status === 404) {
        toast.error(`${platform.charAt(0).toUpperCase() + platform.slice(1)} user "${username}" not found. Please check the username and try again.`);
      } else if (error.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error(`Failed to fetch ${platform} stats. Please try again later.`);
      }
    }
  };

  /**
   * Searches for employees by name or email
   * @async
   * @param {string} searchTerm - Search term
   * @returns {Promise<Array<Employee>>} Matching employees
   */
  const searchEmployees = async (searchTerm) => {
    if (!formData.referred_by && !formData.monetized_by) return;
    
    setLoading(true);
    try {
      const { tokens } = await fetchAuthSession();
      const partnerIds = [...new Set([formData.referred_by, formData.monetized_by].filter(Boolean))];
      
      const searchPromises = partnerIds.map(partnerId =>
        axios.get(API_ENDPOINTS.EMPLOYEES.SEARCH, {
          params: {
            partner_id: partnerId,
            search: searchTerm
          },
          headers: {
            'Authorization': `Bearer ${tokens.accessToken.toString()}`
          }
        })
      );
      
      const responses = await Promise.all(searchPromises);
      
      const combinedResults = responses.reduce((acc, response) => {
        const newEmployees = response.data.filter(newEmp => 
          !acc.some(existingEmp => existingEmp.user_id === newEmp.user_id)
        );
        return [...acc, ...newEmployees];
      }, []);
      
      setMatchingEmployees(combinedResults);
    } catch (error) {
      console.error('Error searching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (employeeSearch.length >= 2) {
        searchEmployees(employeeSearch);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [employeeSearch, formData.referred_by, formData.monetized_by]);

  /**
   * Syncs analytics data with external systems
   * @async
   * @returns {Promise<void>}
   */
  const handleSyncAnalytics = async () => {
    try {
      setSyncStatus('Syncing...');
      
      const { tokens } = await fetchAuthSession();
      const response = await fetch('https://track.snapped.cc/api/analytics/sync/profile', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens.idToken.toString()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          profile_name: profileName,
          snap_id: formData.snap_id,
          user_id: formData.client_id
        })
      });

      if (!response.ok) {
        throw new Error('Sync failed');
      }

      setSyncStatus('Sync successful!');
      setTimeout(() => setSyncStatus(''), 3000);

    } catch (error) {
      console.error('Sync error:', error);
      setSyncStatus('Sync failed');
      setTimeout(() => setSyncStatus(''), 3000);
    }
  };

  /**
   * Fetches approval status for a client
   * @async
   * @param {string} clientId - Client ID
   * @returns {Promise<Object>} Approval status
   */
  const fetchApprovalStatus = async (clientId) => {
    try {
      const { tokens } = await fetchAuthSession();
      const response = await axios.get(API_ENDPOINTS.LEADS.APPROVAL_STATUS(clientId), {
        headers: {
          'Authorization': `Bearer ${tokens.accessToken.toString()}`
        }
      });
      if (response.data && response.data.status === 'success') {
        setFormData(prev => ({
          ...prev, 
          approved: response.data.approved
        }));
      }
    } catch (error) {
      console.error('Error fetching approval status:', error);
    }
  };

  /**
   * Renders social platform input fields
   * @param {string} platform - Social media platform
   * @returns {React.ReactElement} Rendered platform fields
   */
  const renderSocialPlatform = (platform) => {
    return (
      <div key={platform.id} className="-editmodel-social-section">
        <div className="-editmodel-platform-header">
          <FontAwesomeIcon icon={platform.icon} />
          {platform.name}
        </div>

        <div className="-editmodel-platform-content">
          <div className="-editmodel-username-group">
            <input
              type="text"
              name={platform.fields.find(f => f.name.endsWith('Username')).name}
              value={formData[platform.fields.find(f => f.name.endsWith('Username')).name] || ''}
              onChange={handleChange}
              placeholder="Username"
            />
            {platform.fetchStats && (
              <button 
                type="button"
                onClick={() => platform.fetchStats(formData[platform.fields.find(f => f.name.endsWith('Username')).name])}
                className="-editmodel-fetch-button"
                disabled={!formData[platform.fields.find(f => f.name.endsWith('Username')).name]}
              >
                Fetch
              </button>
            )}
          </div>

          {platform.fields.filter(f => f.type === 'number' && !f.name.endsWith('Username')).map(field => (
            <input
              key={field.name}
              type="number"
              name={field.name}
              value={formData[field.name] || ''}
              onChange={handleChange}
              className="-editmodel-stat-input"
              placeholder={field.label}
              step={field.step || '1'}
            />
          ))}
        </div>

        <div className="-editmodel-checkbox-group">
          {platform.fields.filter(f => f.type === 'checkbox').map(field => (
            <div key={field.name} className="-editmodel-checkbox-item">
              <input
                type="checkbox"
                id={field.name}
                name={field.name}
                checked={formData[field.name] || false}
                onChange={handleChange}
              />
              <label htmlFor={field.name}>{field.label}</label>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleAddSplit = async () => {
    if (!selectedPayee || !splitPercentage || !data.client_id) {
      toast.error('Please select a payee and enter a percentage');
      return;
    }

    const percentage = parseInt(splitPercentage);
    if (isNaN(percentage) || percentage <= 0 || percentage > 100) {
      toast.error('Please enter a valid percentage between 1 and 100');
      return;
    }

    try {
      const { tokens } = await fetchAuthSession();
      const response = await fetch(API_ENDPOINTS.PAYMENTS.SAVE_SPLIT_PROFILE, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens.accessToken.toString()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          clientId: data.client_id,
          splits: [...splits, {
            payeeId: selectedPayee._id,
            payeeName: selectedPayee.name,
            companyName: selectedPayee.company_name || '',
            percentage
          }]
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add split');
      }

      await loadSplits();
      setShowSplitModal(false);
      setSelectedPayee(null);
      setSplitPercentage('');
      toast.success('Split added successfully');
    } catch (error) {
      console.error('Error adding split:', error);
      toast.error('Failed to add split');
    }
  };

  const handleRemoveSplit = async (payeeId) => {
    try {
      const { tokens } = await fetchAuthSession();
      const response = await fetch(API_ENDPOINTS.PAYMENTS.DELETE_SPLIT_PROFILE(data.client_id, payeeId), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${tokens.accessToken.toString()}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to remove split');
      }

      await loadSplits();
      toast.success('Split removed successfully');
    } catch (error) {
      console.error('Error removing split:', error);
      toast.error('Failed to remove split');
    }
  };

  const loadPayees = async () => {
    try {
      const { tokens } = await fetchAuthSession();
      const response = await fetch(API_ENDPOINTS.PAYMENTS.SEARCH_PAYEES, {
        headers: {
          'Authorization': `Bearer ${tokens.accessToken.toString()}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load payees');
      }

      const data = await response.json();
      setPayees(data.payees || []);
    } catch (error) {
      console.error('Error loading payees:', error);
      toast.error('Failed to load payees');
    }
  };

  const loadSplits = async () => {
    try {
      const { tokens } = await fetchAuthSession();
      const response = await fetch(API_ENDPOINTS.PAYMENTS.GET_SPLIT_PROFILE(data.client_id), {
        headers: {
          'Authorization': `Bearer ${tokens.accessToken.toString()}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load splits');
      }

      const splitData = await response.json();
      setSplits(splitData.splits || []);
    } catch (error) {
      console.error('Error loading splits:', error);
      toast.error('Failed to load splits');
    }
  };

  useEffect(() => {
    if (isOpen && data?.client_id) {
      loadPayees();
      loadSplits();
    }
  }, [isOpen, data?.client_id]);

  /**
   * Copy creator data to clipboard for spreadsheet
   */
  const handleCopyCreatorData = async () => {
    try {
      // Use CloudFront URL directly since files are in public path
      const snapData = snapMonetizationData || {};
      const videoUrl = snapData.verification_video_s3_url || '';
      
      // Build just the data row (no headers)
      const name = `${formData.First_Legal_Name || ''} ${formData.Last_Legal_Name || ''}`.trim();
      
      const creatorData = [
        name,
        formData.Email_Address || '',
        formData.client_id || '',
        snapData.country || '',
        snapData.city || '',
        snapData.content_category || '',
        snapData.verification_status || '',
        formData.Snap_Username || '',
        formData.IG_Username || '',
        formData.IG_Followers || 0,
        formData.TT_Username || '',
        formData.TT_Followers || 0,
        formData.YT_Username || '',
        formData.YT_Followers || 0,
        snapData.instagram_link || '',
        snapData.tiktok_link || '',
        snapData.youtube_link || '',
        snapData.press_links || '',
        formData.client_bio || '',
        videoUrl
      ];
      
      // Convert to tab-separated values for easy spreadsheet paste
      const tsvData = creatorData.join('\t');
      
      // Copy to clipboard
      await navigator.clipboard.writeText(tsvData);
      toast.success('Creator data copied to clipboard! Paste into spreadsheet.');
      
    } catch (error) {
      console.error('Error copying creator data:', error);
      toast.error('Failed to copy data to clipboard.');
    }
  };

  /**
   * Generate email with creator information
   */
  const handleGenerateEmail = async () => {
    try {
      // Use CloudFront URL directly since files are in public path
      const snapData = snapMonetizationData || {};
      const videoUrl = snapData.verification_video_s3_url || 'N/A';
      
      const name = `${formData.First_Legal_Name || ''} ${formData.Last_Legal_Name || ''}`.trim();
      
      // Build email content
      const subject = `Creator Profile: ${name}`;
      
      const emailBody = `
CREATOR PROFILE: ${name.toUpperCase()}

BASIC INFORMATION:
• Name: ${name}
• Email: ${formData.Email_Address || 'N/A'}
• Client ID: ${formData.client_id || 'N/A'}
• Country: ${snapData.country || 'N/A'}
• City: ${snapData.city || 'N/A'}
• Content Category: ${snapData.content_category || 'N/A'}
• Verification Status: ${snapData.verification_status || 'N/A'}

SOCIAL MEDIA:
• Instagram: @${formData.IG_Username || 'N/A'} (${formData.IG_Followers || 0} followers)
• TikTok: @${formData.TT_Username || 'N/A'} (${formData.TT_Followers || 0} followers)  
• YouTube: @${formData.YT_Username || 'N/A'} (${formData.YT_Followers || 0} followers)
• Snapchat: @${formData.Snap_Username || 'N/A'} (${formData.Snap_Followers || 0} followers)

PROFILE LINKS:
• Instagram: ${snapData.instagram_link || 'N/A'}
• TikTok: ${snapData.tiktok_link || 'N/A'}
• YouTube: ${snapData.youtube_link || 'N/A'}
• Press Coverage: ${snapData.press_links || 'N/A'}
• Verification Video: ${videoUrl}

CREATOR BIO:
${formData.client_bio || 'No bio available'}

---
Generated by PIXEL Creator Management System
${new Date().toLocaleDateString()}
      `.trim();
      
      // Create mailto link
      const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;
      
      // Open default email client
      window.open(mailtoLink);
      
      toast.success('Email template opened in your default email client!');
      
    } catch (error) {
      console.error('Error generating email:', error);
      toast.error('Failed to generate email.');
    }
  };

  /**
   * Handles AI generation of client bio using social media profiles
   * @async
   * @returns {Promise<void>}
   */
  const handleAIGenerate = async () => {
    try {
      // Check if we have any social media profiles
      const socialProfiles = {
        IG_Username: formData.IG_Username,
        TT_Username: formData.TT_Username,
        YT_Username: formData.YT_Username,
        Snap_Username: formData.Snap_Username
      };
      
      const hasProfiles = Object.values(socialProfiles).some(profile => profile && profile.trim());
      
      if (!hasProfiles) {
        toast.error('Please add at least one social media profile before generating bio');
        return;
      }
      
      if (!formData.client_id) {
        toast.error('Client ID is required for bio generation');
        return;
      }
      
      // Show loading state
      const loadingToast = toast.loading('Analyzing social profiles and generating bio...');
      setIsGeneratingBio(true);
      
      try {
        const { tokens } = await fetchAuthSession();
        const response = await axios.post(
          API_ENDPOINTS.LEADS.GENERATE_BIO,
          {
            client_id: formData.client_id,
            social_profiles: socialProfiles
          },
          {
            headers: {
              'Authorization': `Bearer ${tokens.accessToken.toString()}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.data.status === 'success') {
          const bioData = response.data.data;
          
          // Update form data with generated bio information
          setFormData(prev => ({
            ...prev,
            client_bio: bioData.client_bio || '',
            content_type: bioData.content_type || '',
            interests: bioData.interests || [],
            brand_categories: bioData.brand_categories || [],
            audience_demographics: bioData.audience_demographics || {},
            collaboration_history: bioData.collaboration_history || [],
            rate_card: bioData.rate_card || {},
            bio_generated_at: new Date().toISOString(),
            bio_generated_by: user?.username || 'AI System'
          }));
          
          toast.success('Bio generated successfully! Review and edit as needed.', {
            duration: 4000
          });
        } else {
          throw new Error(response.data.message || 'Failed to generate bio');
        }
        
      } catch (error) {
        console.error('Error generating bio:', error);
        
        if (error.response?.status === 400) {
          toast.error(error.response.data.detail || 'Invalid request. Please check your social media profiles.');
        } else if (error.response?.status === 404) {
          toast.error('Client not found or access denied.');
        } else {
          toast.error('Failed to generate bio. Please try again or contact support.');
        }
      } finally {
        toast.dismiss(loadingToast);
        setIsGeneratingBio(false);
      }
      
    } catch (error) {
      console.error('Unexpected error in AI generate:', error);
      toast.error('An unexpected error occurred. Please try again.');
    }
  };

  // Fetch onboarding status when modal opens
  useEffect(() => {
    const fetchOnboardingStatus = async () => {
      if (isOpen && data?.client_id) {
        try {
          const response = await axios.get(`https://track.snapped.cc/api/lead/onboarding-status/${data.client_id}`);
          setOnboardingStatus(response.data);
        } catch (error) {
          console.error('Error fetching onboarding status:', error);
        }
      }
    };

    fetchOnboardingStatus();
  }, [isOpen, data?.client_id]);

  if (!isOpen) return null;

  return (
    <div className="-editmodel-overlay">
      <div className="-editmodel-container">
        <div className="-editmodel-sidebar">
          {sections.map(section => (
            <div
              key={section.id}
              className={`-editmodel-nav-item ${activeSection === section.id ? 'active' : ''}`}
              onClick={() => setActiveSection(section.id)}
            >
              <FontAwesomeIcon icon={section.icon} />
              <span>{section.label}</span>
            </div>
          ))}
        </div>

        <div className="-editmodel-main">
          <div className="-editmodel-header">
            <h2>Edit Lead: {formData.First_Legal_Name} {formData.Last_Legal_Name}</h2>
            <button className="-editmodel-close" onClick={onClose}>&times;</button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="-editmodel-content">
              {/* Onboarding Progress Section */}
              {activeSection === 'onboarding' && (
                <div className="-editmodel-section">
                  <div className="-editmodel-section-header">
                    <h3>Onboarding Progress</h3>
                  </div>
                  {onboardingStatus ? (
                    <div style={{ padding: '32px' }}>
                      {/* Summary Stats */}
                      <div className="onboarding-stats-onboardtrack">
                        <div className="stat-item-onboardtrack">
                          <div className="stat-label-onboardtrack">Current Stage</div>
                          <div className="stat-value-onboardtrack">{onboardingStatus.current_stage_name}</div>
                        </div>
                        <div className="stat-item-onboardtrack">
                          <div className="stat-label-onboardtrack">Completion</div>
                          <div className="stat-value-onboardtrack">{onboardingStatus.completion_percentage}%</div>
                        </div>
                        <div className="stat-item-onboardtrack">
                          <div className="stat-label-onboardtrack">Pending Tasks</div>
                          <div className="stat-value-onboardtrack">{onboardingStatus.pending_automations?.length || 0}</div>
                        </div>
                      </div>

                      {/* Horizontal Progress Bar */}
                      <div className="onboarding-horizontal-progress-onboardtrack">
                        {[
                          { id: 'form_submitted', name: 'Form Submitted', color: '#93c5fd' },
                          { id: 'contract_sent', name: 'Contract Sent', color: '#60a5fa' },
                          { id: 'contract_signed', name: 'Contract Signed', color: '#3b82f6' },
                          { id: 'tiktok_outreach', name: 'TikTok Outreach', color: '#2563eb' },
                          { id: 'snapchat_tips', name: 'Snapchat Tips', color: '#1d4ed8' },
                          { id: 'verified', name: 'Verified (Snap Star)', color: '#1e40af' },
                          { id: 'monetized', name: 'Monetized', color: '#1e3a8a' },
                          { id: 'hyperwallet_setup', name: 'Hyperwallet Setup', color: '#172554' },
                          { id: 'hyperwallet_confirmed', name: 'Hyperwallet Confirmed', color: '#0c1844' }
                        ].map((stage, index) => {
                          const stageData = onboardingStatus.stages?.[index];
                          const isCompleted = stageData?.completed;
                          const isCurrent = index === onboardingStatus.current_stage;
                          
                          return (
                            <div key={stage.id} className="horizontal-stage-item-onboardtrack">
                              <div 
                                className={`horizontal-stage-circle-onboardtrack ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}
                                style={{
                                  backgroundColor: isCompleted ? stage.color : '#f3f4f6',
                                  borderColor: isCurrent ? stage.color : '#e5e7eb'
                                }}
                              >
                                {isCompleted && (
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M13 4L6 11L3 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                )}
                              </div>
                              <div className="horizontal-stage-label-onboardtrack">{stage.name}</div>
                              {stageData?.timestamp && (
                                <div className="horizontal-stage-time-onboardtrack">
                                  {new Date(stageData.timestamp).toLocaleString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit'
                                  })}
                                </div>
                              )}
                              {index < 8 && (
                                <div 
                                  className="horizontal-stage-connector-onboardtrack"
                                  style={{
                                    backgroundColor: isCompleted ? stage.color : '#e5e7eb'
                                  }}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding: '60px', textAlign: 'center', color: '#6b7280' }}>
                      Loading onboarding status...
                    </div>
                  )}
                </div>
              )}
              
              {/* Personal Information Section */}
              {activeSection === 'personal' && (
                <div className="-editmodel-section">
                  <div className="-editmodel-section-header">
                    <h3>Personal Information</h3>
                  </div>
                  <div className="-editmodel-grid">
                    <div className="-editmodel-field">
                      <label>First Name</label>
                      <input
                        name="First_Legal_Name"
                        value={formData.First_Legal_Name || ''}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="-editmodel-field">
                      <label>Last Name</label>
                      <input
                        name="Last_Legal_Name"
                        value={formData.Last_Legal_Name || ''}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="-editmodel-field">
                      <label>Stage Name</label>
                      <input
                        name="Stage_Name"
                        value={formData.Stage_Name || ''}
                        onChange={handleChange}
                      />
                    </div>
                      <div className="-editmodel-field">
                        <label>Email</label>
                        <input
                          type="email"
                          name="Email_Address"
                          value={formData.Email_Address || ''}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="-editmodel-field">
                        <label>Phone Number</label>
                        <input
                          type="tel"
                          name="Phone_Number"
                          value={formData.Phone_Number || ''}
                          onChange={handleChange}
                          placeholder="+1234567890"
                        />
                      </div>
                      <div className="-editmodel-field">
                        <label>Alias Email</label>
                        <input
                          type="email"
                          name="email_alias"
                          value={formData.email_alias || ''}
                          onChange={handleChange}
                          placeholder="Enter alias email"
                        />
                      </div>
                      <div className="-editmodel-field">
                        <label>AWS Email</label>
                        <input
                          type="email"
                          name="aws_email"
                          value={formData.aws_email || ''}
                          onChange={handleChange}
                          placeholder="Enter AWS email"
                          readOnly={!isAdmin}
                        />
                      </div>
                      {isAdmin && (
                        <div className="-editmodel-field">
                          <label>AWS Password</label>
                          <input
                            type="password"
                            name="aws_password"
                            value={formData.aws_password || ''}
                            onChange={handleChange}
                            placeholder="Enter AWS password"
                          />
                        </div>
                      )}
                      <div className="-editmodel-field">
                        <label>Date of Birth</label>
                      <input
                        name="DOB"
                        placeholder="MMDDYYYY"
                        value={formData.DOB || ''}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 8);
                          handleChange({
                            target: {
                              name: 'DOB',
                              value
                            }
                          });
                        }}
                      />
                    </div>
                    <div className="-editmodel-field">
                      <label>Timezone</label>
                      <input
                        name="Timezone"
                        value={formData.Timezone || ''}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="-editmodel-field">
                      <label>Client ID</label>
                      <input
                        name="client_id"
                        value={formData.client_id || ''}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="-editmodel-field">
                      <label>Snap ID</label>
                      <input
                        name="snap_id"
                        value={formData.snap_id || ''}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Client Bio Section */}
              {activeSection === 'bio' && (
                <div className="-editmodel-section">
                  <div className="-editmodel-section-header">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '98%' }}>
                      <h3>Brand Deals & Information</h3>
                      <button 
                        type="button"
                        className={`-editmodel-fetch-button ${isGeneratingBio ? 'generating' : ''}`}
                        onClick={handleAIGenerate}
                        style={{ marginLeft: 'auto' }}
                        disabled={isGeneratingBio}
                      >
                        {isGeneratingBio ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                            Generating...
                          </>
                        ) : (
                          'AI Generate'
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="-editmodel-grid">
                    <div className="-editmodel-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Client Bio</label>
                      <textarea
                        name="client_bio"
                        value={formData.client_bio || ''}
                        onChange={handleChange}
                        placeholder="Enter client bio or use AI Generate to create automatically..."
                        rows="4"
                        style={{ minHeight: '100px', resize: 'vertical' }}
                      />
                    </div>
                    <div className="-editmodel-field">
                      <label>Content Type</label>
                      <select
                        name="content_type"
                        value={formData.content_type || ''}
                        onChange={handleChange}
                      >
                        <option value="">Select Content Type</option>
                        <option value="lifestyle">Lifestyle</option>
                        <option value="comedy">Comedy</option>
                        <option value="dance">Dance</option>
                        <option value="music">Music</option>
                        <option value="fashion">Fashion</option>
                        <option value="beauty">Beauty</option>
                        <option value="fitness">Fitness</option>
                        <option value="food">Food</option>
                        <option value="travel">Travel</option>
                        <option value="gaming">Gaming</option>
                        <option value="tech">Tech</option>
                        <option value="education">Education</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="-editmodel-field">
                      <label>Age</label>
                      <input
                        type="number"
                        name="age"
                        value={formData.age || ''}
                        onChange={handleChange}
                        placeholder="Age"
                        min="13"
                        max="100"
                      />
                    </div>
                    <div className="-editmodel-field">
                      <label>Gender</label>
                      <select
                        name="gender"
                        value={formData.gender || ''}
                        onChange={handleChange}
                      >
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>
                    <div className="-editmodel-field">
                      <label>Location</label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location || ''}
                        onChange={handleChange}
                        placeholder="City, State/Country"
                      />
                    </div>
                    <div className="-editmodel-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Interests (comma-separated)</label>
                      <input
                        type="text"
                        name="interests"
                        value={Array.isArray(formData.interests) ? formData.interests.join(', ') : formData.interests || ''}
                        onChange={(e) => {
                          const interests = e.target.value.split(',').map(i => i.trim()).filter(i => i);
                          handleChange({
                            target: {
                              name: 'interests',
                              value: interests
                            }
                          });
                        }}
                        placeholder="e.g., fashion, travel, fitness, music"
                      />
                    </div>
                    <div className="-editmodel-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Brand Categories (comma-separated)</label>
                      <input
                        type="text"
                        name="brand_categories"
                        value={Array.isArray(formData.brand_categories) ? formData.brand_categories.join(', ') : formData.brand_categories || ''}
                        onChange={(e) => {
                          const categories = e.target.value.split(',').map(c => c.trim()).filter(c => c);
                          handleChange({
                            target: {
                              name: 'brand_categories',
                              value: categories
                            }
                          });
                        }}
                        placeholder="e.g., beauty, fashion, tech, food & beverage"
                      />
                    </div>
                  </div>
                  
                  {/* Rate Card Chart */}
                  <div className="rate-card-section">
                    <h4>Rate Card</h4>
                    <div className="rate-card-grid">
                      <div className="rate-card-header">
                        <div className="rate-card-cell header-cell">Post Type</div>
                        <div className="rate-card-cell header-cell">Instagram</div>
                        <div className="rate-card-cell header-cell">TikTok</div>
                        <div className="rate-card-cell header-cell">X</div>
                        <div className="rate-card-cell header-cell">Facebook</div>
                        <div className="rate-card-cell header-cell">Streaming</div>
                      </div>
                      {['Stories', 'Feed Posts', 'Short Form', 'Long Form', 'Live'].map((postType) => (
                        <div key={postType} className="rate-card-row">
                          <div className="rate-card-cell row-header">{postType}</div>
                          {['instagram', 'tiktok', 'x', 'facebook', 'streaming'].map((platform) => (
                            <div key={platform} className="rate-card-cell">
                              <input
                                type="text"
                                className="rate-card-input"
                                placeholder="$"
                                value={formData.rate_card?.[postType]?.[platform] || ''}
                                onChange={(e) => {
                                  const newRateCard = {
                                    ...formData.rate_card,
                                    [postType]: {
                                      ...formData.rate_card?.[postType],
                                      [platform]: e.target.value
                                    }
                                  };
                                  setFormData(prev => ({
                                    ...prev,
                                    rate_card: newRateCard
                                  }));
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {formData.bio_generated_at && (
                    <div className="bio-generation-info">
                      <small>
                        Bio generated on {new Date(formData.bio_generated_at).toLocaleString()}
                        {formData.bio_generated_by && ` by ${formData.bio_generated_by}`}
                      </small>
                    </div>
                  )}
                </div>
              )}

                  {/* Backend Section */}
                  {activeSection === 'backend' && (
                    <div className="-editmodel-content">
                      {/* Project Management Module */}
                      <div className="pm-module">
                        <div className="pm-module-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <h3>Project Management</h3>
                          <button 
                            className="-editmodel-fetch-button"
                            onClick={async () => {
                              try {
                                // Show loading toast
                                const loadingToast = toast.loading('Refreshing data...');
                                
                                // Refresh all data
                                await loadAssignedEmployees();
                                await loadPayoutInfo();
                                await fetchApprovalStatus(data.client_id);
                                await loadSnapMonetizationData(data.client_id);
                                
                                // Show success toast
                                toast.dismiss(loadingToast);
                                toast.success('Data refreshed successfully');
                              } catch (error) {
                                console.error('Error refreshing data:', error);
                                toast.error('Failed to refresh data');
                              }
                            }}
                            style={{ padding: '4px 8px', fontSize: '14px' }}
                          >
                            <FontAwesomeIcon icon={faSync} /> Refresh Data
                          </button>
                        </div>
                    <div className="pm-company-section">
                      <div className="pm-company-label">Partner Company</div>
                      <input
                        type="text"
                        value={formData.referred_by_name || ''}
                        disabled
                        className="pm-company-input"
                      />
                    </div>
                    {formData.referred_by && (
                      <div className="pm-manager-section">
                        <div className="pm-manager-label">Project Manager Assignment</div>
                        <div className="pm-search-container">
                          <input
                            type="text"
                            value={employeeSearch}
                            onChange={(e) => setEmployeeSearch(e.target.value)}
                            placeholder="Type to search for project managers..."
                            className="pm-search-input"
                          />
                          {employeeSearch.length >= 2 && matchingEmployees.length > 0 && (
                            <div className="pm-search-results">
                              {matchingEmployees.map(emp => (
                                <div 
                                  key={emp.user_id}
                                  className="pm-search-result"
                                  onClick={() => handleAssignEmployee(emp)}
                                >
                                  {emp.first_name} {emp.last_name}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="pm-tags-container">
                          {assignedEmployees.map(emp => (
                            <div key={emp.user_id} className="pm-tag">
                              <span className="pm-tag-name">
                                {emp.first_name} {emp.last_name}
                              </span>
                              <button 
                                onClick={() => handleRemoveEmployee(emp.user_id)}
                                className="pm-tag-remove"
                                title="Remove project manager"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Spotlight Scheduling Module */}
                  <div className="analytics-module">
                    <div className="analytics-header">
                      <h3>Spotlight Scheduling</h3>
                    </div>
                    <div className="analytics-field">
                      <div className="analytics-label">Daily Spotlight Frequency</div>
                      <div className="analytics-input-group">
                        <input
                          type="number"
                          min="1"
                          max="10"
                          step="1"
                          name="spotlight_frequency"
                          value={formData.spotlight_frequency || 2}
                          onChange={handleChange}
                          className="analytics-input"
                        />
                      </div>
                      <small style={{ display: 'block', marginTop: '6px', color: '#6b7280' }}>
                        Determines how many Spotlight posts are scheduled per day. The queue will pair one
                        top performer with supporting videos from recent uploads.
                      </small>
                    </div>
                  </div>

                  {/* Analytics Module */}
                  <div className="analytics-module">
                    <div className="analytics-header">
                      <h3>Analytics</h3>
                    </div>
                    <div className="analytics-field">
                      <div className="analytics-label">Profile Name</div>
                      <div className="analytics-input-group">
                        <input
                          type="text"
                          value={profileName}
                          onChange={(e) => setProfileName(e.target.value)}
                          placeholder="Enter Snapchat profile name"
                          className="analytics-input"
                        />
                        <button
                          type="button"
                          onClick={handleSyncAnalytics}
                          className="analytics-sync-button"
                        >
                          Sync Analytics
                        </button>
                      </div>
                      {syncStatus && (
                        <div className={`analytics-sync-status ${
                          syncStatus === 'Syncing...' ? 'syncing' :
                          syncStatus === 'Sync successful!' ? 'success' :
                          'error'
                        }`}>
                          {syncStatus}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Content Approval Module */}
                  <div className="ca-module">
                    <div className="ca-header">
                      <h3>Content Approval</h3>
                    </div>
                    <div className="ca-approval-row">
                      <input
                        type="checkbox"
                        id="content-approval"
                        name="approved"
                        checked={formData.approved}
                        onChange={handleChange}
                        className="ca-checkbox"
                      />
                      <label htmlFor="content-approval" className="ca-label">
                        Approve Client Content
                      </label>
                    </div>
                    <div className="ca-description">
                      Approving client content will mark their uploads as approved in the system.
                    </div>
                  </div>
                </div>
              )}

              {/* Snap Monetization Section */}
              {activeSection === 'snap-monetization' && (
                <div className="-editmodel-section">
                  <div className="-editmodel-section-header">
                    <h3>Snap Monetization Form</h3>
                  </div>
                  
                  {loadingSnapData ? (
                    <div className="loading-message">Loading Snap Monetization data...</div>
                  ) : snapMonetizationData ? (
                    <div className="snap-monetization-data">
                      <div className="snap-data-grid">
                        <div className="snap-data-item">
                          <label>Submission Date:</label>
                          <span>{new Date(snapMonetizationData.submitted_at).toLocaleDateString()}</span>
                        </div>
                        
                        <div className="snap-data-item">
                          <label>Email:</label>
                          <span>{snapMonetizationData.email}</span>
                        </div>
                        
                        <div className="snap-data-item">
                          <label>Verification Status:</label>
                          <span className={`verification-status ${snapMonetizationData.verification_status.toLowerCase()}`}>
                            {snapMonetizationData.verification_status}
                          </span>
                        </div>
                        
                        <div className="snap-data-item">
                          <label>Snap Username:</label>
                          <span>{snapMonetizationData.snap_username}</span>
                        </div>
                        
                        <div className="snap-data-item">
                          <label>Legal Name:</label>
                          <span>{snapMonetizationData.legal_name}</span>
                        </div>
                        
                        <div className="snap-data-item">
                          <label>Email for Snapchat:</label>
                          <span>{snapMonetizationData.email_for_snapchat}</span>
                        </div>
                        
                        <div className="snap-data-item">
                          <label>Country:</label>
                          <span>{snapMonetizationData.country}</span>
                        </div>
                        
                        <div className="snap-data-item">
                          <label>City:</label>
                          <span>{snapMonetizationData.city}</span>
                        </div>
                        
                        <div className="snap-data-item">
                          <label>Content Category:</label>
                          <span>{snapMonetizationData.content_category}</span>
                        </div>
                        
                        <div className="snap-data-item">
                          <label>Instagram:</label>
                          <a href={snapMonetizationData.instagram_link} target="_blank" rel="noopener noreferrer">
                            {snapMonetizationData.instagram_link}
                          </a>
                        </div>
                        
                        <div className="snap-data-item">
                          <label>TikTok:</label>
                          <a href={snapMonetizationData.tiktok_link} target="_blank" rel="noopener noreferrer">
                            {snapMonetizationData.tiktok_link}
                          </a>
                        </div>
                        
                        {snapMonetizationData.youtube_link && (
                          <div className="snap-data-item">
                            <label>YouTube:</label>
                            <a href={snapMonetizationData.youtube_link} target="_blank" rel="noopener noreferrer">
                              {snapMonetizationData.youtube_link}
                            </a>
                          </div>
                        )}
                        
                        <div className="snap-data-item">
                          <label>Press Links:</label>
                          <a href={snapMonetizationData.press_links} target="_blank" rel="noopener noreferrer">
                            {snapMonetizationData.press_links}
                          </a>
                        </div>
                        
                        {snapMonetizationData.verification_video_filename && (
                          <div className="snap-data-item verification-video">
                            <label>Verification Video:</label>
                            <div>
                              <span className="video-filename">{snapMonetizationData.verification_video_filename}</span>
                              <div style={{ marginTop: '8px' }}>
                                <button 
                                  onClick={async () => {
                                    try {
                                      const { tokens } = await fetchAuthSession();
                                      const response = await fetch(API_ENDPOINTS.SNAP_MONETIZATION.VIDEO_DOWNLOAD(data.client_id), {
                                        headers: {
                                          'Authorization': `Bearer ${tokens.accessToken.toString()}`
                                        }
                                      });
                                      const result = await response.json();
                                      if (result.download_url) {
                                        window.open(result.download_url, '_blank');
                                      }
                                    } catch (error) {
                                      console.error('Error getting video URL:', error);
                                      toast.error('Failed to load video');
                                    }
                                  }}
                                  style={{
                                    display: 'inline-block',
                                    padding: '4px 8px',
                                    backgroundColor: '#2563eb',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  View/Download Video
                                </button>
                                {snapMonetizationData.verification_video_size && (
                                  <small style={{ marginLeft: '8px', color: '#6b7280' }}>
                                    ({(snapMonetizationData.verification_video_size / 1024 / 1024).toFixed(1)} MB)
                                  </small>
                                )}
                              </div>
                            </div>
                            <small>Video file submitted for identity verification</small>
                          </div>
                        )}
                        
                        {snapMonetizationData.updated_at && (
                          <div className="snap-data-item">
                            <label>Last Updated:</label>
                            <span>{new Date(snapMonetizationData.updated_at).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="snap-monetization-actions">
                        <button 
                          type="button"
                          className="-editmodel-fetch-button"
                          onClick={() => window.open('/snap-monetization', '_blank')}
                        >
                          View/Edit Form
                        </button>
                        <button 
                          type="button"
                          className="-editmodel-fetch-button"
                          onClick={handleCopyCreatorData}
                          style={{ marginLeft: '10px', backgroundColor: '#28a745' }}
                        >
                          Copy to Spreadsheet
                        </button>
                        <button 
                          type="button"
                          className="-editmodel-fetch-button"
                          onClick={handleGenerateEmail}
                          style={{ marginLeft: '10px', backgroundColor: '#17a2b8' }}
                        >
                          Generate Email
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="no-snap-data">
                      <p>No Snap Monetization form has been submitted for this client.</p>
                      <button 
                        type="button"
                        className="-editmodel-fetch-button"
                        onClick={() => window.open('/snap-monetization', '_blank')}
                      >
                        Submit Form
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Messaging Section */}
              {activeSection === 'messaging' && (
                <div className="-editmodel-section">
                  <div className="-editmodel-section-header">
                    <h3>Messaging Integration</h3>
                  </div>
                  
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(2, 1fr)', 
                    gap: '24px',
                    marginBottom: '24px'
                  }}>
                    <div style={{ 
                      padding: '20px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0'
                    }}>
                      <label style={{ 
                        fontWeight: '600', 
                        fontSize: '13px', 
                        color: '#555',
                        marginBottom: '8px',
                        display: 'block',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Messaging Platform
                      </label>
                      <select
                        name="message_location"
                        value={formData.message_location || 'iMessage'}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '10px',
                          fontSize: '14px',
                          borderRadius: '4px',
                          border: '1px solid #ccc',
                          backgroundColor: '#fff',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        {messagingPlatforms.map(platform => (
                          <option key={platform.value} value={platform.value}>
                            {platform.label}
                          </option>
                        ))}
                      </select>
                      <small style={{ display: 'block', color: '#888', fontSize: '12px', marginTop: '6px' }}>
                        Primary messaging platform for this client
                      </small>
                    </div>

                    <div style={{ 
                      padding: '20px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0'
                    }}>
                      <label style={{ 
                        fontWeight: '600', 
                        fontSize: '13px', 
                        color: '#555',
                        marginBottom: '8px',
                        display: 'block',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Phone Number
                      </label>
                      <div style={{ 
                        fontSize: '16px', 
                        fontWeight: '500',
                        color: '#222',
                        marginBottom: '6px',
                        fontFamily: 'monospace'
                      }}>
                        {formData.Phone_Number || 'Not set'}
                      </div>
                      <small style={{ color: '#888', fontSize: '12px' }}>
                        Primary phone number for messaging
                      </small>
                    </div>
                    
                    <div style={{ 
                      padding: '20px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0'
                    }}>
                      <label style={{ 
                        fontWeight: '600', 
                        fontSize: '13px', 
                        color: '#555',
                        marginBottom: '8px',
                        display: 'block',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        E.164 Format
                      </label>
                      <div style={{ 
                        fontSize: '16px', 
                        fontWeight: '500',
                        color: formData.normalized_phone_number ? '#222' : '#999',
                        marginBottom: '6px',
                        fontFamily: 'monospace'
                      }}>
                        {formData.normalized_phone_number || 'Not synced'}
                      </div>
                      <small style={{ color: '#888', fontSize: '12px' }}>
                        E.164 format for automated messaging
                      </small>
                    </div>
                    
                    <div style={{ 
                      padding: '20px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0'
                    }}>
                      <label style={{ 
                        fontWeight: '600', 
                        fontSize: '13px', 
                        color: '#555',
                        marginBottom: '8px',
                        display: 'block',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Group Chat ID
                      </label>
                      <div style={{ 
                        fontSize: '14px', 
                        fontWeight: '500',
                        color: formData.group_chat_id ? '#222' : '#999',
                        marginBottom: '6px',
                        fontFamily: 'monospace',
                        wordBreak: 'break-all'
                      }}>
                        {formData.group_chat_id || 'Not synced'}
                      </div>
                      <small style={{ color: '#888', fontSize: '12px' }}>
                        Unique identifier for message thread
                      </small>
                    </div>
                    
                    <div style={{ 
                      padding: '20px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0'
                    }}>
                      <label style={{ 
                        fontWeight: '600', 
                        fontSize: '13px', 
                        color: '#555',
                        marginBottom: '8px',
                        display: 'block',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Message Location
                      </label>
                      <div style={{ 
                        fontSize: '16px', 
                        fontWeight: '500',
                        color: formData.message_location ? '#222' : '#999',
                        marginBottom: '6px'
                      }}>
                        {formData.message_location || 'Not synced'}
                      </div>
                      <small style={{ color: '#888', fontSize: '12px' }}>
                        Messaging platform (iMessage, SMS, WhatsApp, Telegram)
                      </small>
                    </div>
                  </div>
                  
                  <div style={{ 
                    padding: '20px', 
                    backgroundColor: '#fff', 
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    marginBottom: '24px'
                  }}>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="-editmodel-fetch-button"
                        onClick={async () => {
                          if (!formData.client_id) {
                            toast.error('Client ID is required');
                            return;
                          }
                          
                          try {
                            setSyncingMessaging(true);
                            const { tokens } = await fetchAuthSession();
                            
                            const response = await axios.post(
                              API_ENDPOINTS.MESSAGING.SYNC_GROUP_CHAT,
                              { client_id: formData.client_id },
                              {
                                headers: {
                                  'Authorization': `Bearer ${tokens.accessToken.toString()}`
                                }
                              }
                            );
                            
                            if (response.data.success) {
                              // Update form data with synced info
                              setFormData(prev => ({
                                ...prev,
                                group_chat_id: response.data.group_chat_id,
                                message_location: response.data.message_location,
                                normalized_phone_number: response.data.normalized_phone_number,
                              messaging_last_sync: new Date().toISOString()
                            }));
                            
                            toast.success(response.data.message);
                          } else {
                            toast.error(response.data.message);
                          }
                        } catch (error) {
                          console.error('Error syncing messaging:', error);
                          toast.error(error.response?.data?.detail || 'Failed to sync messaging info');
                        } finally {
                          setSyncingMessaging(false);
                        }
                        }}
                        disabled={syncingMessaging || !formData.Phone_Number}
                        style={{ 
                          padding: '10px 20px',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}
                      >
                        {syncingMessaging ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                            {' '}Syncing...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faSync} />
                            {' '}Sync Group Chat ID
                          </>
                        )}
                      </button>
                      
                      {formData.messaging_last_sync && (
                        <small style={{ color: '#666', fontSize: '13px' }}>
                          Last synced: {new Date(formData.messaging_last_sync).toLocaleString()}
                        </small>
                      )}
                    </div>
                    <small style={{ display: 'block', marginTop: '12px', color: '#666', lineHeight: '1.5' }}>
                      Search imessages_raw database for this client's phone number and link to group chat
                    </small>
                  </div>
                  
                  <div style={{ 
                    padding: '20px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div className="contract-status-cell" style={{ marginBottom: '8px' }}>
                      <input
                        type="checkbox"
                        id="auto_messaging_enabled"
                        name="auto_messaging_enabled"
                        className="contract-status-checkbox"
                        checked={formData.auto_messaging_enabled !== false}
                        onChange={(e) => {
                          const enabled = e.target.checked;
                          setFormData(prev => ({
                            ...prev,
                            auto_messaging_enabled: enabled
                          }));
                        }}
                        style={{ marginRight: '10px' }}
                      />
                      <label htmlFor="auto_messaging_enabled" style={{ 
                        fontSize: '15px', 
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}>
                        Enable Auto-Messaging
                      </label>
                    </div>
                    <small style={{ display: 'block', color: '#666', lineHeight: '1.5' }}>
                      When enabled, client will receive automated messages. Turn off to exclude from messaging campaigns.
                    </small>
                  </div>
                </div>
              )}

              {/* Social Media Section */}
              {activeSection === 'socials' && (
                <div className="-editmodel-section">
                  <div className="-editmodel-section-header">
                    <h3>Social Media Profiles</h3>
                  </div>
                  <div className="-editmodel-social-table">
                    <div className="-editmodel-table-header">
                      <div className="-editmodel-header-cell">Platform</div>
                      <div className="-editmodel-header-cell">Username</div>
                      <div className="-editmodel-header-cell">Verified</div>
                      <div className="-editmodel-header-cell">Followers</div>
                      <div className="-editmodel-header-cell">Rank</div>
                      <div className="-editmodel-header-cell">Views Rank</div>
                    </div>
                    {socialPlatforms.map(platform => {
                      const usernameField = platform.fields.find(f => f.name.endsWith('Username'));
                      const followersField = platform.fields.find(f => f.name.includes('Followers'));
                      const engagementField = platform.fields.find(f => f.name.includes('Engagement'));
                      const rankField = platform.fields.find(f => f.name.includes('Rank') && !f.name.includes('Views'));
                      const viewsRankField = platform.fields.find(f => f.name.includes('Views_Rank'));
                      const verifiedField = platform.fields.find(f => 
                        f.name.includes('Verified') || f.name === 'Snap_Star'
                      );

                      return (
                        <div key={platform.id} className="-editmodel-platform-row">
                          <div className="-editmodel-platform-name">
                            {platform.name}
                          </div>
                          <div className="-editmodel-username-cell">
                            <div className="-editmodel-username-group">
                              <input
                                type="text"
                                name={usernameField.name}
                                value={formData[usernameField.name] || ''}
                                onChange={handleChange}
                                placeholder="Username"
                              />
                              {platform.fetchStats && (
                                <button 
                                  type="button"
                                  onClick={() => platform.fetchStats(formData[usernameField.name])}
                                  className="-editmodel-fetch-button"
                                  disabled={!formData[usernameField.name]}
                                >
                                  Fetch
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="-editmodel-verified-cell">
                            {verifiedField && (
                              <input
                                type="checkbox"
                                name={verifiedField.name}
                                checked={formData[verifiedField.name] || false}
                                onChange={handleChange}
                                className="-editmodel-verified-checkbox"
                              />
                            )}
                          </div>
                          <div className="-editmodel-stat-cell">
                            {followersField && (
                              <input
                                type="number"
                                name={followersField.name}
                                value={formData[followersField.name] || ''}
                                onChange={handleChange}
                                placeholder="Followers"
                                className="-editmodel-stat-input"
                              />
                            )}
                          </div>
                          <div className="-editmodel-stat-cell">
                            {(engagementField || rankField) && (
                              <input
                                type="number"
                                name={(engagementField || rankField).name}
                                value={formData[(engagementField || rankField).name] || ''}
                                onChange={handleChange}
                                placeholder={engagementField ? 'Engagement Rate' : 'Rank'}
                                step={engagementField ? '0.01' : '1'}
                                className="-editmodel-stat-input"
                              />
                            )}
                          </div>
                          <div className="-editmodel-stat-cell">
                            {viewsRankField && (
                              <input
                                type="number"
                                name={viewsRankField.name}
                                value={formData[viewsRankField.name] || ''}
                                onChange={handleChange}
                                placeholder="Views Rank"
                                className="-editmodel-stat-input"
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Contract Status Section */}
              {activeSection === 'contract' && (
                <div className="-editmodel-section">
                  <div className="contract-status-header">
                    <h3>Contract Status</h3>
                  </div>
                  <div className="contract-status-grid">
                    <div className="contract-status-cell">
                      <input
                        type="checkbox"
                        id="is_contractout"
                        name="is_contractout"
                        className="contract-status-checkbox"
                        checked={formData.is_contractout || false}
                        onChange={handleChange}
                      />
                      <label htmlFor="is_contractout">Contract Out</label>
                    </div>
                    <div className="contract-status-cell">
                      <input
                        type="checkbox"
                        id="is_signed"
                        name="is_signed"
                        className="contract-status-checkbox"
                        checked={formData.is_signed || false}
                        onChange={handleChange}
                      />
                      <label htmlFor="is_signed">Signed</label>
                    </div>
                    <div className="contract-status-cell">
                      <input
                        type="checkbox"
                        id="is_groupchat"
                        name="is_groupchat"
                        className="contract-status-checkbox"
                        checked={formData.is_groupchat || false}
                        onChange={handleChange}
                      />
                      <label htmlFor="is_groupchat">In Groupchat</label>
                    </div>
                    <div className="contract-status-cell">
                      <input
                        type="checkbox"
                        id="is_dead"
                        name="is_dead"
                        className="contract-status-checkbox"
                        checked={formData.is_dead || false}
                        onChange={handleChange}
                      />
                      <label htmlFor="is_dead">Dead</label>
                    </div>
                  </div>
                </div>
              )}

              {/* Payout Information Section */}
              {activeSection === 'payout' && (
                <>
                  <div className="-editmodel-section">
                    <div className="-editmodel-section-header">
                      <h3>Payout Information</h3>
                    </div>
                    <div className="-editmodel-grid">
                      <div className="-editmodel-field">
                        <label>Payout Email</label>
                        <div className="payout-email-group">
                          <input
                            type="email"
                            className="payout-email-input"
                            value={formData.payout_email || ''}
                            onChange={(e) => handleChange({
                              target: {
                                name: 'payout_email',
                                value: e.target.value
                              }
                            })}
                            placeholder="Enter payout email"
                          />
                          <button 
                            className="payout-sync-button"
                            onClick={handleSyncPayout}
                            disabled={isSyncing || !formData.payout_email}
                          >
                            {isSyncing ? 'Syncing...' : 'Sync'}
                          </button>
                        </div>
                      </div>
                      
                      <div className="-editmodel-field">
                        <label>Client Payout Percentage</label>
                        <input
                          type="number"
                          name="client_payout_percent"
                          value={formData.client_payout_percent || 0}
                          onChange={handleChange}
                          min="0"
                          max="100"
                          step="0.01"
                          placeholder="Enter client payout percentage"
                        />
                        <small style={{ display: 'block', marginTop: '4px', color: '#666' }}>
                          What percentage of gross does the client receive? (0-100)
                        </small>
                      </div>
                      
                      <div className="-editmodel-field">
                        <label>Company Net Percentage</label>
                        <input
                          type="number"
                          name="company_net_percent"
                          value={formData.company_net_percent || 100}
                          onChange={handleChange}
                          min="0"
                          max="100"
                          step="0.01"
                          placeholder="Enter company net percentage"
                        />
                        <small style={{ display: 'block', marginTop: '4px', color: '#666' }}>
                          What percentage does the company keep after client payout AND referral fees? (0-100)
                        </small>
                      </div>

                      {payoutInfo && (
                        <div className="payout-info">
                          <div className="payout-stat">
                            <label>Total Paid to Date:</label>
                            <span>${payoutInfo.total_paid?.toLocaleString()}</span>
                          </div>
                          <div className="payout-stat">
                            <label>Year to Date:</label>
                            <span>${payoutInfo.year_to_date?.toLocaleString()}</span>
                          </div>
                          <div className="payout-stat">
                            <label>Quarter to Date:</label>
                            <span>${payoutInfo.quarter_to_date?.toLocaleString()}</span>
                          </div>
                          <div className="payout-sync-status">
                            Last synced: {payoutInfo.last_sync ? 
                              new Date(payoutInfo.last_sync).toLocaleString() : 
                              'Never'}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Payment Splits Section - Admin Only */}
                  {isAdmin && (
                    <div className="-editmodel-section">
                      <div className="-editmodel-section-header">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                          <h3>Payment Splits</h3>
                          <button 
                            className="-editmodel-fetch-button"
                            onClick={() => setShowSplitModal(true)}
                            style={{ marginLeft: 'auto' }}
                          >
                            Add Split
                          </button>
                        </div>
                      </div>
                      
                      {splits.length > 0 ? (
                        <div className="splits-table">
                          <div className="splits-table-header">
                            <div>Payee</div>
                            <div>Company</div>
                            <div>Percentage</div>
                            <div>Actions</div>
                          </div>
                          {splits.map((split) => (
                            <div key={split.payeeId} className="splits-table-row">
                              <div>{split.payeeName}</div>
                              <div>{split.companyName || 'N/A'}</div>
                              <div>{split.percentage}%</div>
                              <div>
                                <button
                                  className="split-remove-button"
                                  onClick={() => handleRemoveSplit(split.payeeId)}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="no-splits-message">
                          No payment splits configured
                        </div>
                      )}
                    </div>
                  )}

                  <div className="payout-history">
                    <div className="payout-history-header">
                      <h4>Payout History</h4>
                    </div>
                    <div className="payout-history-list">
                      {payoutInfo?.creator_pulls?.length > 0 ? (
                        payoutInfo.creator_pulls.map((pull, index) => (
                          <div key={index} className="payout-item">
                            <div className="payout-date">
                              {new Date(pull.pull_date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </div>
                            <div className="payout-amount">
                              ${pull.pull_amount.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                            </div>
                            <div className="payout-status">
                              Completed
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="no-payouts-message">
                          No payout history available
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="-editmodel-actions">
              <button 
                type="button" 
                className="-editmodel-button -editmodel-button-secondary" 
                onClick={onClose}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="-editmodel-button -editmodel-button-primary"
                disabled={isSaving || !hasChanges}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
            
            {/* Save Status Message */}
            {saveStatus.message && (
              <div className={`-editmodel-save-status -editmodel-save-status-${saveStatus.type}`}>
                {saveStatus.message}
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Split Modal */}
      {showSplitModal && (
        <div className="split-modal-overlay">
          <div className="split-modal">
            <div className="split-modal-header">
              <h3>Add Payment Split</h3>
              <button onClick={() => setShowSplitModal(false)}>&times;</button>
            </div>
            <div className="split-modal-content">
              <div className="split-modal-field">
                <label>Payee</label>
                <select 
                  value={selectedPayee?._id || ''} 
                  onChange={(e) => {
                    const payee = payees.find(p => p._id === e.target.value);
                    setSelectedPayee(payee);
                  }}
                >
                  <option value="">Select a Payee</option>
                  {payees.map(payee => (
                    <option key={payee._id} value={payee._id}>
                      {payee.name} {payee.company_name ? `(${payee.company_name})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="split-modal-field">
                <label>Percentage</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={splitPercentage}
                  onChange={(e) => setSplitPercentage(e.target.value)}
                  placeholder="Enter percentage (1-100)"
                />
              </div>
            </div>
            <div className="split-modal-actions">
              <button 
                className="split-modal-cancel"
                onClick={() => setShowSplitModal(false)}
              >
                Cancel
              </button>
              <button 
                className="split-modal-add"
                onClick={handleAddSplit}
                disabled={!selectedPayee || !splitPercentage}
              >
                Add Split
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Onboarding Modal */}
      <OnboardingModal
        isOpen={onboardingModalOpen}
        onClose={() => setOnboardingModalOpen(false)}
        clientId={data?.client_id}
        clientName={`${formData.First_Legal_Name || ''} ${formData.Last_Legal_Name || ''}`.trim()}
      />
    </div>
  );
};

export default EditModal; 